/**
 * Sprinklr Live Chat SDK configuration.
 *
 * The App ID decides which Sprinklr application — and so which pool of live
 * agents — an escalation lands in. Production traffic has to reach the
 * production app; local dev and the lower lifecycles have to reach the dev app,
 * or a test escalation opens a real case in front of a real agent.
 *
 * `SPRINKLR_CONFIG.APP_ID` is a getter, resolved when it is read at escalation
 * time rather than frozen at module load, because on the server there is no
 * hostname to read yet.
 */

import { getHostname } from '../../shared/utils/window-utils';

export const SPRINKLR_APP_IDS = {
  dev: '697cc150c7bd9954ea5e9b24_app_100874948',
  prod: '6a231183637cc8595092eda5_app_100900283',
};

/**
 * Server-set feature switch that forces the dev app. It reaches the page in
 * `window.__EXPERIENCE_PROPS__.expConfiguration`, and is absent from that object
 * unless it has been signalled to the SSR by request header.
 *
 * It only forces one direction. Off production it changes nothing — the hostname
 * already resolves to the dev app — so its purpose is validating on a real
 * production page without an escalation reaching a real agent. Nothing can point
 * a lower lifecycle at the production app, which is deliberate.
 */
export const SPRINKLR_USE_DEV_APP_CONFIG_KEY = 'magic-apron-use-sprinklr-dev-app';

// Exact match with a dev-app fallback (deliberate direction) — see
// .claude/context/live-agent.md ("App-ID resolution").
const PROD_HOSTNAME = 'www.homedepot.com';

const LOCAL_HOSTNAMES = ['localhost', 'localhost.homedepot.com', '127.0.0.1'];

const isLocalDev = LOCAL_HOSTNAMES.includes(getHostname());

/**
 * To test with the REAL Sprinklr SDK locally, set this to true before starting
 * the dev server:
 *
 *   SPRINKLR_REAL=true npm start
 *
 * Or manually flip FORCE_REAL_SPRINKLR to true below.
 */
// const FORCE_REAL_SPRINKLR = typeof process !== 'undefined' && process.env?.SPRINKLR_REAL === 'true';
const FORCE_REAL_SPRINKLR = true;

// Values in expConfiguration are raw — unlike useConfigService, nothing has
// coerced 'true' to a boolean.
const isDevAppForced = () => {
  if (typeof window === 'undefined') return false;
  const value = window.__EXPERIENCE_PROPS__?.expConfiguration?.[SPRINKLR_USE_DEV_APP_CONFIG_KEY];
  return value === true || String(value).trim().toLowerCase() === 'true';
};

export const SPRINKLR_CONFIG = {
  CDN_HOST: 'https://live-chat-static.sprinklr.com',
  ENVIRONMENT: 'prod2',
  LOCALE: 'en',
  MOCK_MODE: isLocalDev && !FORCE_REAL_SPRINKLR,

  get APP_ID() {
    return (getHostname() === PROD_HOSTNAME && !isDevAppForced())
      ? SPRINKLR_APP_IDS.prod
      : SPRINKLR_APP_IDS.dev;
  },
};
